import React from 'react';

/**
 * Wraps React.lazy with automatic page reload on chunk load failures.
 *
 * After a deployment the browser may hold a stale index.html that references
 * chunk filenames from the previous build. When the server can't find the old
 * chunk it returns the SPA fallback (text/html) instead of JS, which causes a
 * TypeError. This wrapper catches that error and reloads the page once so the
 * browser fetches the latest index.html.
 *
 * A sessionStorage flag prevents infinite reload loops if the new build also
 * fails for an unrelated reason.
 */
const RELOAD_FLAG = 'chunk_reload';

/**
 * Recover from a failed chunk load by reloading the page once. On a second
 * failure the flag is cleared and the error is re-thrown so an error boundary
 * can surface it, rather than looping.
 */
function recoverFromChunkFailure<T>(error: unknown): Promise<T> {
  let hasReloaded = false;
  try {
    hasReloaded = !!sessionStorage.getItem(RELOAD_FLAG);
  } catch {
    // sessionStorage unavailable — skip retry logic
    throw error;
  }

  if (!hasReloaded) {
    try {
      sessionStorage.setItem(RELOAD_FLAG, '1');
    } catch {
      // Cannot persist flag — skip reload to avoid infinite loop risk
      throw error;
    }
    window.location.reload();
    // Return a promise that never resolves — the reload will take over
    return new Promise<never>(() => {});
  }

  // Already reloaded once — clear the flag and let the error propagate
  try {
    sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    // Ignore — flag clearance is non-critical
  }
  throw error;
}

export function importWithRetry<T>(importFn: () => Promise<T>): Promise<T> {
  return importFn().then(
    (module) => {
      // A failed chunk load can *resolve to `undefined`* instead of rejecting:
      // when the `vite:preloadError` handler (main.tsx) calls preventDefault(),
      // Vite's __vitePreload swallows the load error and fulfils the import with
      // `undefined`. Handing that to React.lazy crashes with the cryptic
      // "undefined is not an object (evaluating '_result.default')"
      // (ARTIC-HUB-15R). Treat a nullish module as a chunk failure and recover.
      if (module == null) {
        return recoverFromChunkFailure<T>(
          new Error('Dynamic import resolved to an empty module (stale chunk)'),
        );
      }
      return module;
    },
    (error: unknown) => recoverFromChunkFailure<T>(error),
  );
}

export function lazyWithRetry<T extends React.ComponentType<unknown>>(
  importFn: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return React.lazy(() => importWithRetry(importFn));
}

/**
 * Call this on successful app hydration to clear the reload flag so that
 * future deploys can trigger a fresh reload.
 */
export function clearChunkReloadFlag(): void {
  try {
    sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    // sessionStorage unavailable — non-critical
  }
}
